import { useQuery } from '@tanstack/react-query';
import { queryOptionsGetFeatures } from '../api/shared/settingsApi';
import { FeatureFlag } from '../types/commonTypes';

/**
 * Hook to check if a feature flag (or multiple feature flags) is enabled.
 *
 * @param featureFlags - The feature flag name(s) to check. Can be a single string or an array of strings.
 *               If an array, returns true only if ALL flags are enabled.
 * @returns An object with:
 *   - isEnabled: boolean indicating if the feature flag(s) are enabled
 *   - isLoading: boolean indicating if the features are still loading
 */
export const useFeatureFlag = (featureFlags: FeatureFlag) => {
  const featuresQuery = useQuery(queryOptionsGetFeatures());
  const { data: features, isLoading } = featuresQuery;

  const areFlagsEnabled: boolean = isLoading
    ? false
    : Array.isArray(featureFlags)
      ? featureFlags.every((flag) => features?.[flag] === 'true')
      : features?.[featureFlags] === 'true';

  return [areFlagsEnabled, featuresQuery] as const;
};
